import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSeo } from "../lib/seo";
import {
  BOOKFREELY_LONG_DESCRIPTION,
  HOW_IT_WORKS_CUSTOMERS,
  HOW_IT_WORKS_BUSINESSES,
} from "../lib/brand";

/**
 * /about — public marketing page. Communicates what BookFreely is to
 * visitors, prospective businesses, and Stripe reviewers looking at the
 * live site during platform verification.
 */
export default function AboutPage() {
  useSeo({
    title: "About BookFreely — Booking, without the stress",
    description:
      "BookFreely is an online marketplace connecting customers with independent local service businesses. Learn how bookings, verification, and payouts work.",
    path: "/about",
  });
  return (
    <div className="App" data-testid="about-page">
      <Header hideSearch />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>
          About BookFreely
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight" style={{ color: "var(--gf-text)" }}>
          Booking, without the stress.
        </h1>
        <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--gf-text-soft)" }}>
          {BOOKFREELY_LONG_DESCRIPTION.what_it_is}
        </p>

        <Section title="Our mission">
          <p>{BOOKFREELY_LONG_DESCRIPTION.mission}</p>
        </Section>

        <Section title="How it works">
          <p>{BOOKFREELY_LONG_DESCRIPTION.how_it_works}</p>
        </Section>

        <Section title="Why it matters">
          <p>{BOOKFREELY_LONG_DESCRIPTION.why_it_matters}</p>
        </Section>

        <Section title="For customers">
          <ol className="mt-2 space-y-3" data-testid="how-it-works-customers">
            {HOW_IT_WORKS_CUSTOMERS.map((s) => (
              <StepRow key={s.n} n={s.n} t={s.t} d={s.d} />
            ))}
          </ol>
        </Section>

        <Section title="For businesses">
          <ol className="mt-2 space-y-3" data-testid="how-it-works-businesses">
            {HOW_IT_WORKS_BUSINESSES.map((s) => (
              <StepRow key={s.n} n={s.n} t={s.t} d={s.d} />
            ))}
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/signup?role=business"
              data-testid="about-cta-list-business"
              className="rounded-full px-5 py-2.5 text-sm font-medium"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              List your business
            </Link>
            <Link
              to="/services"
              data-testid="about-cta-browse-services"
              className="rounded-full border px-5 py-2.5 text-sm"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Browse services
            </Link>
          </div>
        </Section>

        <div
          className="mt-14 rounded-2xl border p-6"
          style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
        >
          <div className="font-display text-lg" style={{ color: "var(--gf-text)" }}>Questions?</div>
          <p className="text-sm mt-1" style={{ color: "var(--gf-text-soft)" }}>
            Reach us at <a href="mailto:bookfreely.support@gmail.com" className="underline">bookfreely.support@gmail.com</a>.
            Read our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10" data-testid={`about-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>{title}</h2>
      <div className="mt-2 text-sm leading-relaxed space-y-3" style={{ color: "var(--gf-text-soft)" }}>
        {children}
      </div>
    </section>
  );
}

function StepRow({ n, t, d }) {
  return (
    <li className="flex items-start gap-4">
      <span className="font-display text-lg flex-shrink-0" style={{ color: "var(--gf-accent)" }}>{n}</span>
      <div>
        <div className="text-sm font-medium" style={{ color: "var(--gf-text)" }}>{t}</div>
        <div className="text-sm mt-0.5" style={{ color: "var(--gf-text-muted)" }}>{d}</div>
      </div>
    </li>
  );
}
